import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Tree,
  Input,
  Upload,
  message as messagePopUp,
  Modal,
  Button,
  Spin,
  Typography
} from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  FileZipOutlined,
  FormOutlined,
  ClearOutlined
} from '@ant-design/icons';
import {
  getListFiles,
  addFiles,
  editFileName,
  deleteFile,
  deleteMonitoreos,
  deleteDownloads,
  deleteTemps,
  makeDirectory,
  downloadFiles,
  massiveDownload
} from 'actions/recursosGluster.actions';
import { fileToBase64 } from 'utils/fileToBase64';
import './Gluster.scss';

const { Text } = Typography;
const { DirectoryTree } = Tree;

export function Gluster() {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [messageModal, setMessageModal] = useState({ id: '', title: '', content: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [fileUpload, setFileUpload] = useState([]);
  const [fileUploadBase64, setFileUploadBase64] = useState();
  const [glusterPath, setGlusterPath] = useState({
    documentName: '',
    documentPath: ''
  });
  const [newDirName, setNewDirName] = useState('');
  const [process, setProcess] = useState('');
  const { userAgent } = window.navigator;
  const isWindows = userAgent.indexOf('Windows') > -1;
  const separator = isWindows ? '\\' : '/';
  const navigate = useNavigate();
  const deleteSelected = useRef(false);
  const downloadSelected = useRef(false);
  const downloadSelectedDir = useRef(false);
  const editSelected = useRef(false);
  const autoSearch = useRef(false);
  const allowAccept = useRef(false);
  const oldDirName = useRef('');

  const { data } = useSelector((state) => state.validarUsuario);
  const allowedUsersList = [
    'jonathan.viveros',
    'angela.inclan',
    'laura.viggers',
    'abigail.belloc',
    'clara.gallardo'
  ];

  if (!allowedUsersList.includes(data.name)) {
    navigate('/');
  }

  const selectFileToDelete = () => {
    deleteSelected.current = true;
  };
  const selectFileToDownload = () => {
    downloadSelected.current = true;
  };
  const selectDirToDownload = () => {
    downloadSelectedDir.current = true;
  };
  const selectFileToEdit = () => {
    editSelected.current = true;
  };

  const addFunc = (fileName) => {
    // eslint-disable-next-line no-return-assign
    return (
      <>
        <p>{fileName}</p>
        <div className="funcButtons">
          <Button onClick={() => selectFileToEdit()}>
            <FormOutlined />
          </Button>
          {fileName.includes('.') ? (
            <Button onClick={() => selectFileToDownload()}>
              <DownloadOutlined />
            </Button>
          ) : (
            <Button onClick={() => selectDirToDownload()}>
              <FileZipOutlined />
            </Button>
          )}
          <Button onClick={() => selectFileToDelete()}>
            <DeleteOutlined />
          </Button>
        </div>
      </>
    );
  };

  const getFileList = async (enter = false) => {
    const selectedFileIndex = fileList.findIndex(({ key }) => key === glusterPath.documentPath);
    if (
      glusterPath.documentPath === '' ||
      selectedFileIndex !== -1 ||
      enter ||
      autoSearch.current
    ) {
      try {
        setLoading(true);
        const { success, documentList, message } = await getListFiles(glusterPath);
        if (success) {
          const fileListCustom = documentList.map(({ key, title, isLeaf }) => ({
            key,
            title: addFunc(title),
            switcherIcon: isLeaf ? (
              <FileOutlined />
            ) : (
              ({ expanded }) => (expanded ? <FolderOpenOutlined /> : <FolderOutlined />)
            )
          }));
          setFileList(fileListCustom);
        } else {
          messagePopUp.error(message);
        }
      } catch (error) {
        console.error('Error en la petición: ', error);
      }
      autoSearch.current = false;
      setLoading(false);
    }
  };

  const uploadFile = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      // const { success, message } = await addFiles(formData);
      const { success, message } = await addFiles({
        documentPath: glusterPath.documentPath,
        documentName: fileUpload[0].name,
        documentBase64: fileUploadBase64
      });
      if (success) {
        setFileUpload([]);
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      messagePopUp.error(`El archivo ${glusterPath.documentName} no se pudo subir.`);
    }
    getFileList(true);
    setLoading(false);
  };

  const editFile = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      const { success, message } = await editFileName({
        documentPath: glusterPath.documentPath + oldDirName.current,
        documentName: glusterPath.documentName,
        newDocumentName: glusterPath.documentPath + newDirName
      });
      if (success) {
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('No se pudo cambiar el nombre al archivo o la carpeta');
    }
    setNewDirName('');
    getFileList(true);
    setLoading(false);
    editSelected.current = false;
  };
  const deleteSelectedFile = async (clear = 0) => {
    setModalOpen(false);
    try {
      setLoading(true);

      const { success, message } = await deleteFile({
        typeId: clear,
        documentName: glusterPath.documentName,
        documentPath: glusterPath.documentPath
      });
      if (success) {
        const parts = glusterPath.documentPath.split('\\');
        const lastFile = parts[parts.length - 1];
        if (!lastFile.includes('.')) {
          setGlusterPath({
            documentName: '',
            documentPath: glusterPath.documentPath.replace(lastFile, '')
          });
          autoSearch.current = true;
        }
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error(`No se pudo eliminar ${glusterPath.documentName}`);
    }
    setLoading(false);
  };

  const cleanMonitoreosDir = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      const { success, message } = await deleteMonitoreos({ documentPath: process });
      if (success) {
        autoSearch.current = true;
        getFileList();
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('No se pudo eliminar el contenido de la carpeta de Monitoreos');
    }
    setLoading(false);
  };

  const cleanDownloadsDir = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      const { success, message } = await deleteDownloads();
      if (success) {
        autoSearch.current = true;
        getFileList();
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('No se pudo eliminar el contenido de la carpeta de Descargas');
    }
    setLoading(false);
  };

  const cleanTemplFiles = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      const { success, message } = await deleteTemps();
      if (success) {
        autoSearch.current = true;
        getFileList();
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('Hubo un error en la petición.');
    }
    setLoading(false);
  };

  const makeNewDirectory = async () => {
    setModalOpen(false);
    try {
      setLoading(true);
      const { success, message } = await makeDirectory(
        `${glusterPath.documentPath}\\${newDirName}`
      );
      // eslint-disable-next-line no-unused-expressions
      success ? messagePopUp.success(message) : messagePopUp.error(message);
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('Hubo un error en la petición');
    }
    setNewDirName('');
    getFileList(true);
    setLoading(false);
  };

  const downloadFile = async (filePath) => {
    try {
      const { success, message, file } = await downloadFiles(filePath);
      if (success) {
        const byteCharacters = atob(file);
        const byteNumbers = new Array(byteCharacters.length);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filePath?.documentName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        messagePopUp.success(message);
      } else {
        messagePopUp.error(message);
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('Hubo un error en la descarga');
    }
  };

  const downloadDir = async (dirPath) => {
    try {
      const { success, message, file } = await massiveDownload(dirPath);
      if (success) {
        messagePopUp.success(message);
        const byteCharacters = atob(file);
        const byteNumbers = new Array(byteCharacters.length);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'file.zip');
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        messagePopUp.error('Hubo un error en la descarga masiva');
      }
    } catch (error) {
      console.error('Error en la petición: ', error);
      messagePopUp.error('Hubo un error en la descarga masiva');
    }
  };

  const onAcceptClick = () => {
    switch (messageModal.id) {
      case 'upload':
        uploadFile();
        break;
      case 'edit':
        editFile();
        break;
      case 'delete':
        deleteSelectedFile();
        break;
      case 'cleanMonitoreoX':
        cleanMonitoreosDir();
        break;
      case 'cleanDownloads':
        cleanDownloadsDir();
        break;
      case 'cleanTempFiles':
        cleanTemplFiles();
        break;
      case 'makeDir':
        makeNewDirectory();
        break;
      case 'clear':
        deleteSelectedFile(1);
        break;
      default:
        console.error('Function not specified');
    }
  };

  const onCancelClick = () => {
    if (messageModal.id === 'delete') {
      if (glusterPath.documentName === '') {
        const parts = glusterPath.documentPath.split('\\');
        const lastFile = parts[parts.length - 1];
        setGlusterPath({ documentPath: glusterPath.documentPath.replace(lastFile, '') });
      }
    }
    setNewDirName('');
    editSelected.current = false;
    setModalOpen(false);
  };

  const uploaderHandleChange = (info) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1);
    setFileUpload(newFileList);
  };

  allowAccept.current =
    (messageModal.id === 'edit' || messageModal.id === 'makeDir') &&
    (newDirName.trim() === '' ||
      newDirName.includes('/') ||
      newDirName.includes('\\') ||
      newDirName.includes('.') ||
      newDirName.includes('*'));

  const onSelectFile = (keys) => {
    console.log('serparator', separator);
    // FOR CAPA AND PRUEBAS
    // const parts = keys[0].split('//');
    // FOR LOCAL
    const parts = keys[0].split(separator);
    console.log('parts', parts);
    const lastFile = parts[parts.length - 1];
    let newGluster;
    if (lastFile.includes('.')) {
      newGluster = { documentName: lastFile, documentPath: keys[0].replace(lastFile, '') };
    } else if (editSelected.current || downloadSelected.current) {
      newGluster = { documentName: '', documentPath: keys[0].replace(lastFile, '') };
    } else if (downloadSelectedDir.current) {
      newGluster = {
        documentName: '',
        documentPath: keys[0].replace(lastFile, ''),
        documentZipPath: keys[0]
      };
    } else {
      newGluster = { documentName: '', documentPath: keys[0] };
    }
    console.log('newGluster', newGluster);
    setGlusterPath(newGluster);
    if (deleteSelected.current) {
      setMessageModal({
        id: 'delete',
        title: `¿Está seguro que desea eliminar ${
          lastFile.includes('.') ? 'el archivo' : 'el directorio y todo su contenido'
        }?`,
        content: lastFile
      });
    }
    if (downloadSelected.current) {
      if (lastFile.includes('.')) {
        downloadFile(newGluster);
      }
      downloadSelected.current = false;
    }
    if (downloadSelectedDir.current) {
      if (!lastFile.includes('.')) {
        downloadDir(newGluster);
      }
      downloadSelectedDir.current = false;
    }
    if (editSelected.current) {
      const splitedName = lastFile.split('.');
      if (!lastFile.includes('.')) {
        // eslint-disable-next-line prefer-destructuring
        oldDirName.current = splitedName[0];
      } else {
        oldDirName.current = '';
      }
      setMessageModal({
        id: 'edit',
        title: `Cambiar nombre del ${lastFile.includes('.') ? 'archivo' : 'directorio'}`,
        content: (
          <Input
            defaultValue=""
            onChange={(e) => setNewDirName(e.target.value)}
            placeholder={splitedName[0]}
            addonAfter={lastFile.includes('.') ? `.${splitedName[1]}` : ''}
          />
        )
      });
    }
  };

  const goBackDir = () => {
    let path = glusterPath.documentPath;
    if (glusterPath.documentPath.endsWith(separator)) {
      path = path.slice(0, -1);
    }
    const index = path.lastIndexOf(separator);
    if (index === -1) {
      setGlusterPath({ documentName: '', documentPath: '' });
    } else {
      const newPath = glusterPath.documentPath.substring(0, index);
      autoSearch.current = true;
      setGlusterPath({
        documentName: '',
        documentPath: newPath
      });
    }
  };

  const getFullBase64 = async (file) => {
    const dataBase64 = await fileToBase64(file);
    setFileUploadBase64(dataBase64.split(',')[1]);
  };

  const handleBeforeUpload = (file) => {
    getFullBase64(file);
    return false;
  };

  useEffect(() => {
    if (messageModal.id !== '') {
      setModalOpen(true);
    }
  }, [messageModal]);

  useEffect(() => {
    if (!deleteSelected.current) {
      getFileList();
    } else {
      deleteSelected.current = false;
    }
  }, [glusterPath]);

  return (
    <div className="files-manager-panel">
      <Spin spinning={loading}>
        <div className="files-manager-title">
          <h1>Bienvenido al administrador de archivos {data?.name?.replace('.', ' ')}</h1>
        </div>
        <div className="path-input">
          <Input
            autoSize
            // eslint-disable-next-line no-return-assign
            onChange={(e) => setGlusterPath({ documentName: '', documentPath: e.target.value })}
            placeholder="dir_example/dir_child/"
            suffix={
              <>
                <Button
                  onClick={goBackDir}
                  disabled={glusterPath.documentPath === ''}
                  className="arrow-left-button">
                  <ArrowLeftOutlined />
                </Button>
                <Button
                  onClick={() =>
                    setMessageModal({
                      id: 'clear',
                      title: '¿Está seguro que desea limpiar el contenido de esta carpeta?',
                      content: glusterPath.documentPath
                    })
                  }
                  disabled={glusterPath.documentPath === ''}
                  className="arrow-left-button">
                  <ClearOutlined />
                </Button>
              </>
            }
            value={glusterPath.documentPath?.replace(/\\/g, '/')}
            className="path-input-text"
            addonBefore={<p className="path-label">.../glusterFS/simac/</p>}
            addonAfter={
              <>
                <Button onClick={getFileList} className="search-button">
                  Buscar
                </Button>
                <Button
                  type="primary"
                  onClick={() =>
                    setMessageModal({
                      id: 'makeDir',
                      title: 'Crear nuevo directorio en:',
                      content: (
                        <div className="make-dir">
                          <Text>
                            .../glusterFS/simac/{glusterPath.documentPath.replace('\\', '/')}
                          </Text>
                          <Input
                            onChange={(e) => setNewDirName(e.target.value)}
                            placeholder="Nombre del directorio"
                          />
                        </div>
                      )
                    })
                  }
                  className="mkdir-button">
                  Crear
                </Button>
              </>
            }
            onKeyDown={(e) => e.key === 'Enter' && getFileList(true)}
          />
        </div>
        <div className="directory-panel">
          <div className="directory-tree">
            <DirectoryTree
              onSelect={onSelectFile}
              showLine
              showIcon={false}
              treeData={fileList}
              className="directory-tree-content"
            />
          </div>
          <div className="directory-uploader">
            <Upload
              fileList={fileUpload}
              beforeUpload={handleBeforeUpload}
              multiple={false}
              onChange={uploaderHandleChange}
              className="uploader-button">
              <Button icon={<UploadOutlined />} className="button-select-file">
                Select File
              </Button>
            </Upload>
            <Button
              type="primary"
              onClick={() =>
                setMessageModal({
                  id: 'upload',
                  title: '¿Está seguro de subir el siguiente archivo al directorio especificado?',
                  content: fileUpload[0]?.name
                })
              }
              disabled={fileUpload.length === 0}
              className="button-upload">
              Subir a gluster
            </Button>
          </div>
        </div>
        <h2>Eliminar contenido de:</h2>
        <div className="extra-buttons-panel">
          <div className="monitoreos-form">
            <Input
              placeholder="proceso"
              style={{ marginRight: '5px', width: '300px' }}
              allowClear
              onChange={(e) => setProcess(e.target.value)}
            />
            <Button
              type="primary"
              disabled={process === ''}
              onClick={() =>
                setMessageModal({
                  id: 'cleanMonitoreoX',
                  title: `¿Está seguro que desea eliminar el contenido del directorio de monitoreos?`,
                  content: process
                })
              }
              className="extra-button">
              Monitoreos
            </Button>
          </div>
          <Button
            type="primary"
            onClick={() =>
              setMessageModal({
                id: 'cleanDownloads',
                title: '¿Está seguro que desea eliminar el contenido del directorio?',
                content: 'Descargas'
              })
            }
            className="extra-button">
            Descargas
          </Button>
          <Button
            type="primary"
            onClick={() =>
              setMessageModal({
                id: 'cleanTempFiles',
                title: '¿Está seguro que desea eliminar el contenido del directorio?',
                content: 'Temporales'
              })
            }
            className="extra-button">
            Archivos temporales
          </Button>
        </div>
        <Modal
          title={messageModal.title}
          centered
          open={modalOpen}
          onOk={onAcceptClick}
          onCancel={onCancelClick}
          okButtonProps={{
            disabled: allowAccept.current
          }}
          className="modal-gluster-confirmation">
          <div>{messageModal.content}</div>
        </Modal>
      </Spin>
    </div>
  );
}

export default Gluster;
